import React, { useState, useEffect } from "react";
import { RouteComponentProps } from "react-router";

import { Typography, CircularProgress, Dialog, Snackbar, DialogTitle, Paper, makeStyles } from "@material-ui/core";

import { ServerFailed } from "../errors/serverFailed";
import { PageNotFound } from "../errors/pageNotFound";

import { ServerResponse } from "../../services/server/OldWebClient";
import { BuilderAssetService, SaveBuilderAssetRequest } from "../../services/BuilderAssetService";
 
import { BuilderAsset } from "../../entities/BuilderAsset";
import AppConfig from "../../entities/AppConfig";

import { ApplicationUser } from "../../entities/ApplicationUser";
import { isActiveMakerSubscription } from "../../entities/MakerSubscription";
import { BuilderHome } from '../builder/home';
import { useUser } from "../../utilities/useUser";
import { BuilderPreviewContext } from '../../utilities/useBuilderPreview'
import { SignInRegisterDialog } from './components/signInRegisterDialog';
import { SignUpOfferDialog } from './components/signUpOfferDialog';
import { MakerCheckOutDialog } from "./components/makerCheckoutDialog";
import { BundleCheckoutDialog } from "./components/bundleCheckoutDialog";
import { ProfitWell } from "../../utilities/Tracking/ProfitWell";
import { EventTracking } from "../../utilities/Tracking/EventTracking";
import { useIsMobile } from "../../components/Mui3WidthProvider";
import useLocalStorage from "../../utilities/useLocalStorage";

const useStyles = makeStyles(() => ({
	"@global": {
		h1: {
			lineHeight: "1.4em",
		},
		h2: {
			lineHeight: "1.4em",
		},
	},
	dialogPaper: {
		touchAction: "none",
		backgroundColor: "transparent",
		"-webkit-tap-highlight-color": "transparent",
		boxShadow: "none"
	},
	backButton: {
		color: "#fff", 
		cursor: "pointer",
		'&:hover': {
			color: "#EEE",
		}
	},
	dialogContentPaper: {
		height: "fit-content",
		overflowY: "auto",
	}
}));

interface Props extends RouteComponentProps<{ id: string }> {
    config: AppConfig;
	appUser: ApplicationUser | null;
}

export function BuilderPreview(props: Props) {
	const { match, history, appUser } = props;
	const userContext = useUser();
	const isMobile = useIsMobile();
	const classes = useStyles();

	const [user, setUser] = useState<ApplicationUser | null>(null);
	const [sermonAsset, setSermonAsset] = useState<BuilderAsset | undefined>();
	const [serverLoading, setServerLoading] = useState<boolean>(true);
	const [showOffer, setShowOffer] = useState<boolean>(false);
	const [showMakerCheckout, setShowMakerCheckout] = useState<boolean>(false);
	const [showBundleCheckout, setBundleCheckout] = useState<boolean>(false);
	const [serverFailed, setServerFailed] = useState<boolean>(false);
	const [pageNotFound, setPageNotFound] = useState<boolean>(false);
	const [snackbarMessage, setSnackbarMessage] = useState<string | undefined>();

	useEffect(() => {
		async function mountComponent(){
			document.title = "Sermon Maker - Builder";
			const sermonId = match.params.id;
			localStorage.setItem("registerParams", `?builderPreviewId=${sermonId}`)

			try {
				const sermonAssetResponse = await BuilderAssetService.getSermonAsBuilderAsset(sermonId);
				
				if (ServerResponse.isError(sermonAssetResponse)) {
					if (sermonAssetResponse.statusCode === 404) {
						setPageNotFound(true);
						setServerLoading(false);
					}
					if (sermonAssetResponse.statusCode === 500) {
						setServerFailed(true);
						setServerLoading(false);
					}
					return;
				} else if (ServerResponse.isModelValidation(sermonAssetResponse)) {
					setServerFailed(true);
					setServerLoading(false);
					return;
				}

				if(appUser && isActiveMakerSubscription(appUser.makerSubscription)){
					saveAssetToLibrary(sermonAssetResponse.data);
				} else {
					setSermonAsset(sermonAssetResponse.data);
					setUser(appUser)
					setServerLoading(false);
				}
			} catch {
				setPageNotFound(true);
				setServerLoading(false);
			}
		}

		mountComponent();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if(user === null){
			return;
		}
		userContext.updateUser(user);
		
		const hasActiveMakerSubscription = user && isActiveMakerSubscription(user.makerSubscription);
		
		if(hasActiveMakerSubscription && sermonAsset){
			saveAssetToLibrary(sermonAsset);
		} else {
			setShowOffer(true);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user])

	const toggleMakerCheckOut = () => {
		setShowOffer(false);
		setShowMakerCheckout(true);
	}
	const toggleBundleCheckOut = () => {
		setShowOffer(false);
		setBundleCheckout(true);
	}
	const handleBackClick = () => {
		if(showBundleCheckout || showMakerCheckout){
			setShowOffer(true);
			setShowMakerCheckout(false);
			setBundleCheckout(false);
		} else {
			history.goBack();
		}
	}

	const saveAssetToLibrary = async(asset:BuilderAsset ) => {
		const knownUser = user ?? appUser;

		if(!knownUser)
			return;

		const saveAssetRequest:SaveBuilderAssetRequest = {
			...asset,
			id: undefined,
		} 

		const response = await BuilderAssetService.save(saveAssetRequest);

		if(ServerResponse.isSuccess(response)){
			EventTracking.Builder.onImportSCSermon(Number(match.params.id))
			ProfitWell.onUserAuthenticated(knownUser.email);
			userContext.updateUser(knownUser);
			history.push(`/build/${response.data.id}`);
		} else {
			setServerFailed(true);
		}
	}

	if (pageNotFound) {
		return <PageNotFound refreshPage={true} errorMessage="We’re sorry, but we couldn’t find the Sermon Asset you are trying to save." />;
	}
	if (serverFailed) {
		return <ServerFailed errorMessage="We’re sorry, but we’re having trouble loading your Sermon Asset. Please try again" />;
	}

	const builderAsset = sermonAsset;

	if(serverLoading) {
		return (
			<div style={{ textAlign: "center" }}>
				<CircularProgress style={{ marginTop: "50px" }} />
				<br />
				<Typography variant="body1" color="textSecondary" style={{ textAlign: "center" }}>
					Loading...
				</Typography>
			</div>
		);
	}

	return (
		<div>
			<BuilderPreviewContext.Provider 
				value={{ 
					user: user, 
					updateUser: (updatedUser) => setUser(updatedUser),
					showSnackBar: (message) => setSnackbarMessage(message)
				}} >
				<BuilderHome user={null} {...props} sermonAsset={builderAsset} />
				<Dialog open={true} disableBackdropClick disableEscapeKeyDown classes={{paper: classes.dialogPaper }} fullScreen={isMobile}>
					<DialogTitle style={{padding: 0}}>
						<span onClick={handleBackClick} className={classes.backButton}>{"< Back"}</span>
					</DialogTitle>
					<Paper className={classes.dialogContentPaper} elevation={2}>
						{ !user && <SignInRegisterDialog />}
						{ showOffer && <SignUpOfferDialog onSelectMakerCheckout={() => toggleMakerCheckOut()} onSelectedBundleCheckOut={() => toggleBundleCheckOut()}/>}
						{ showMakerCheckout && <MakerCheckOutDialog />}
						{ showBundleCheckout && <BundleCheckoutDialog />}
					</Paper>
				</Dialog>
				<Snackbar
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'left',
					}}
					open={snackbarMessage !== undefined}
					autoHideDuration={3000}
					onClose={() => setSnackbarMessage(undefined)}
					ContentProps={{
						'aria-describedby': 'message-id',
					}}
					message={<span id="message-id">{snackbarMessage}</span>}
					/>
			</BuilderPreviewContext.Provider>
		</div>
	);
}